import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Title } from "lola-framework-ui-test";
import { FloatingField, type FloatingFieldColors } from "./FloatingField";
import { useStore } from "../store/globalState";
import { useCatalogPayers, useCatalogStates } from "../hooks/useCatalog";
import { resolveStateOptions, stateName } from "../utils/states";
import { useCashPickupHabits } from "../hooks/useCashPickupHabits";
import type { CashPickupHabit } from "../api/cash-pickup-habits";
import type { IEstimateOption } from "../services/api";
import {
  catalogPayerDisplayName,
  dedupeOfferableCashPayers,
  dedupeOfferableCashPayersForState,
  payerCodesEquivalent,
  resolveCashPickupOption,
} from "../utils/payerCode";
import { BackArrowLight, CloseLight } from "../assets/navIcons";

function payoutFieldColors(view: {
  background: string;
  title: string;
  bodyCopy: string;
  subtitile: string;
  errorColor?: string;
}): FloatingFieldColors {
  return {
    background: view.background,
    title: view.title,
    bodyCopy: view.bodyCopy,
    inactiveColor: view.subtitile,
    errorColor: view.errorColor,
  };
}

export interface CashPickupSelection {
  stateCode: string;
  payerId: string;
  payerName: string;
  option: IEstimateOption | null;
}

export interface CashPickupDetailsSlotProps {
  destinationCountry: string;
  /**
   * Funding rail frozen at origination — scopes catalog states/payers so we
   * don't mix CRB_INTERMEX MX_* state lists with INCOMM ANY:ANY cash-deposit
   * routes (same bug as send without this scope).
   */
  fundingMethod?: string | null;
  /** Corridor origin for the same catalog scope. */
  originCountryCode?: string | null;
  options: IEstimateOption[];
  optionsLoading?: boolean;
  value?: { stateCode?: string; payerId?: string } | null;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (result: CashPickupSelection) => void;
}

const bareStateCode = (stateCode: string | undefined | null): string | null => {
  if (!stateCode?.trim()) return null;
  const trimmed = stateCode.trim().toUpperCase();
  const dash = trimmed.indexOf("-");
  if (dash > 0 && dash <= 3) return trimmed.slice(dash + 1) || null;
  return trimmed;
};

/**
 * Second step after choosing cash reception: state + cash network (payer).
 * Mirrors the request-flow CashPickupForm pattern.
 */
export const CashPickupDetailsSlot: React.FC<CashPickupDetailsSlotProps> = ({
  destinationCountry,
  fundingMethod,
  originCountryCode,
  options,
  optionsLoading,
  value,
  onBack,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn, textColorBtn } = colorConfig;
  const fieldColors = payoutFieldColors(colorConfig);

  const { habits: remoteHabits, loading: habitsLoading } =
    useCashPickupHabits(destinationCountry);

  const catalogScope = {
    ...(originCountryCode ? { originCountryCode } : {}),
    ...(fundingMethod ? { fundingMethod } : {}),
  };

  const { data: states, loading: statesLoading } = useCatalogStates(
    destinationCountry,
    { payoutMethod: "CASH_PICKUP", ...catalogScope }
  );

  const { data: countryPayers, loading: countryPayersLoading } =
    useCatalogPayers({
      ...catalogScope,
      destinationCountryCode: destinationCountry,
      payoutMethod: "CASH_PICKUP",
    });

  const availableCountryPayers = useMemo(
    () =>
      (countryPayers ?? []).filter((p) => p.effectiveStatus === "AVAILABLE"),
    [countryPayers]
  );

  const offerablePayerCodes = useMemo(
    () => new Set(dedupeOfferableCashPayers(options)),
    [options]
  );

  const isAnyStateMode =
    !statesLoading &&
    !countryPayersLoading &&
    (states ?? []).length === 0 &&
    availableCountryPayers.length > 0;

  const [stateCode, setStateCode] = useState<string | null>(
    value?.stateCode ?? null
  );
  const [payerId, setPayerId] = useState<string | null>(value?.payerId ?? null);

  const offerablePayerCodesForState = useMemo(
    () => new Set(dedupeOfferableCashPayersForState(options, stateCode)),
    [options, stateCode]
  );

  const stateOptions = useMemo(() => {
    const all = resolveStateOptions(destinationCountry, states, isAnyStateMode);
    if (isAnyStateMode || optionsLoading || offerablePayerCodes.size === 0) {
      return all;
    }
    const catalogPayersByState = new Map(
      (states ?? []).map((s) => [
        s.stateCode.toUpperCase(),
        s.payerCodes ?? [],
      ])
    );
    return all.filter((s) => {
      if (dedupeOfferableCashPayersForState(options, s.code).length > 0) {
        return true;
      }
      const catalogCodes = catalogPayersByState.get(s.code.toUpperCase()) ?? [];
      return catalogCodes.some((code) =>
        [...offerablePayerCodes].some((offerable) =>
          payerCodesEquivalent(offerable, code)
        )
      );
    });
  }, [
    destinationCountry,
    states,
    isAnyStateMode,
    optionsLoading,
    options,
    offerablePayerCodes,
  ]);

  useEffect(() => {
    if (!stateCode) return;
    if (stateOptions.length === 0) return;
    const stillOffered = stateOptions.some(
      (s) => s.code.toUpperCase() === stateCode.toUpperCase()
    );
    if (!stillOffered) {
      setStateCode(null);
      setPayerId(null);
    }
  }, [stateOptions, stateCode]);

  const { data: scopedPayers, loading: scopedPayersLoading } = useCatalogPayers(
    !isAnyStateMode && stateCode
      ? {
          ...catalogScope,
          destinationCountryCode: destinationCountry,
          payoutMethod: "CASH_PICKUP",
          stateCode,
        }
      : null
  );

  const payerCatalog = useMemo(
    () => [...availableCountryPayers, ...(scopedPayers ?? [])],
    [availableCountryPayers, scopedPayers]
  );

  const savedHabits = useMemo(() => {
    if (optionsLoading) return [];
    return remoteHabits.filter((h) =>
      [...offerablePayerCodes].some((code) =>
        payerCodesEquivalent(code, h.payerId)
      )
    );
  }, [remoteHabits, offerablePayerCodes, optionsLoading]);

  const payerList = useMemo(() => {
    if (optionsLoading || !stateCode) return [];
    const fromQuote = [...offerablePayerCodesForState];
    if (fromQuote.length > 0) {
      return fromQuote
        .map((code) => {
          const name = catalogPayerDisplayName(code, payerCatalog);
          return name ? { id: code, name } : null;
        })
        .filter((row): row is { id: string; name: string } => row != null)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const source = isAnyStateMode
      ? availableCountryPayers
      : (scopedPayers ?? []);
    return source
      .filter((p) => p.effectiveStatus === "AVAILABLE")
      .filter(
        (p) =>
          offerablePayerCodes.size === 0 ||
          [...offerablePayerCodes].some((c) =>
            payerCodesEquivalent(c, p.payerCode)
          )
      )
      .map((p) => {
        const name = catalogPayerDisplayName(p.payerCode, payerCatalog);
        return name ? { id: p.payerCode, name } : null;
      })
      .filter((row): row is { id: string; name: string } => row != null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [
    optionsLoading,
    stateCode,
    offerablePayerCodesForState,
    payerCatalog,
    isAnyStateMode,
    availableCountryPayers,
    scopedPayers,
    offerablePayerCodes,
  ]);

  const effectivePayerId = useMemo(() => {
    if (!payerId) return null;
    const exact = payerList.find((p) => p.id === payerId);
    if (exact) return exact.id;
    const aliased = payerList.find((p) =>
      payerCodesEquivalent(p.id, payerId)
    );
    return aliased?.id ?? null;
  }, [payerId, payerList]);

  const payersLoading =
    Boolean(optionsLoading) ||
    (isAnyStateMode
      ? countryPayersLoading
      : Boolean(stateCode) && scopedPayersLoading);

  const catalogCoversPayer = Boolean(
    effectivePayerId &&
      (isAnyStateMode ? availableCountryPayers : scopedPayers ?? []).some(
        (p) =>
          p.effectiveStatus === "AVAILABLE" &&
          payerCodesEquivalent(p.payerCode, effectivePayerId)
      )
  );

  const canResolveSku = Boolean(
    stateCode &&
      effectivePayerId &&
      resolveCashPickupOption(options, effectivePayerId, stateCode)
  );

  const valid = Boolean(canResolveSku || (stateCode && catalogCoversPayer));

  const labeledHabits = useMemo(() => {
    if (countryPayersLoading || countryPayers == null) return [];
    return savedHabits
      .map((habit) => {
        const payerLabel = catalogPayerDisplayName(
          habit.payerId,
          countryPayers
        );
        const bare = bareStateCode(habit.stateCode) ?? habit.stateCode;
        const stateLabel = stateName(destinationCountry, bare) || bare;
        if (!payerLabel || !stateLabel) return null;
        return { habit, title: payerLabel, subtitle: stateLabel };
      })
      .filter((row): row is NonNullable<typeof row> => row != null);
  }, [
    savedHabits,
    countryPayers,
    countryPayersLoading,
    destinationCountry,
  ]);

  const habitsBusy =
    habitsLoading || Boolean(optionsLoading) || countryPayersLoading;
  const showSavedBlock = habitsBusy || labeledHabits.length > 0;

  const commit = (nextState: string, nextPayer: string, payerName: string) => {
    onSubmit({
      stateCode: nextState,
      payerId: nextPayer,
      payerName,
      option: resolveCashPickupOption(options, nextPayer, nextState),
    });
  };

  const selectHabit = (habit: CashPickupHabit, payerName: string) => {
    const bare = bareStateCode(habit.stateCode) ?? habit.stateCode;
    commit(bare, habit.payerId, payerName);
  };

  return (
    <section className="payout-details-slot">
      <header className="payout-details-header">
        <button
          type="button"
          className="payout-header-action payout-back-btn"
          aria-label={t("common.back")}
          onClick={onBack}
        >
          <BackArrowLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
        <Title
          title={t("payout.details.CASH_PICKUP.title")}
          align="center"
          color={bodyCopy}
        />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label={t("common.cancel")}
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>

      <form
        className="payout-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid || !stateCode || !effectivePayerId) return;
          const payer = payerList.find((p) => p.id === effectivePayerId);
          if (!payer?.name) return;
          commit(stateCode, effectivePayerId, payer.name);
        }}
      >
        {showSavedBlock && (
          <section
            className="payout-saved"
            aria-label={t("payout.savedDestinations.aria")}
            aria-busy={habitsBusy}
          >
            <span className="payout-field-label">
              {t("payout.savedDestinations.label")}
            </span>
            <div role="list" className="payout-saved-list">
              {habitsBusy ? (
                <>
                  {Array.from({ length: 2 }, (_, i) => (
                    <div
                      key={i}
                      className="payout-saved-skeleton"
                      aria-hidden="true"
                    >
                      <span className="payout-saved-skeleton-line" />
                      <span className="payout-saved-skeleton-line short" />
                    </div>
                  ))}
                </>
              ) : (
                labeledHabits.map(({ habit, title, subtitle }) => (
                  <button
                    key={`${habit.stateCode}:${habit.payerId}`}
                    type="button"
                    role="listitem"
                    className="payout-saved-item"
                    onClick={() => selectHabit(habit, title)}
                  >
                    <span className="payout-saved-title">{title}</span>
                    <span className="payout-hint">{subtitle}</span>
                  </button>
                ))
              )}
            </div>
            <span className="payout-hint payout-or-new">
              {t("payout.savedDestinations.orNew")}
            </span>
          </section>
        )}

        <div className="payout-field">
          <FloatingField
            label={t("payout.fields.state")}
            value={stateCode ?? ""}
            colors={fieldColors}
          >
            <select
              style={{ color: bodyCopy, cursor: "pointer" }}
              value={stateCode ?? ""}
              onChange={(e) => {
                setStateCode(e.target.value || null);
                setPayerId(null);
              }}
            >
              <option value="" />
              {stateOptions.map((s) => (
                <option key={`${destinationCountry}-${s.code}`} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
          </FloatingField>
          {!statesLoading && !optionsLoading && stateOptions.length === 0 && (
            <span className="payout-hint">{t("payout.fields.noStates")}</span>
          )}
        </div>

        {stateCode && (
          <div className="payout-field">
            <FloatingField
              label={t("payout.fields.payer")}
              value={effectivePayerId ?? ""}
              colors={fieldColors}
            >
              <select
                style={{ color: bodyCopy, cursor: "pointer" }}
                value={effectivePayerId ?? ""}
                disabled={payersLoading}
                onChange={(e) => setPayerId(e.target.value || null)}
              >
                <option value="" />
                {payerList.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </FloatingField>
            {payersLoading && (
              <span className="payout-hint">{t("payout.fields.payerLoading")}</span>
            )}
            {!payersLoading && payerList.length === 0 && (
              <span className="payout-hint">{t("payout.fields.noPayers")}</span>
            )}
          </div>
        )}

        <Button
          type="submit"
          size="large"
          disabled={!valid}
          background={backgroundBtn}
          color={textColorBtn}
        >
          {t("common.continue")}
        </Button>
      </form>
    </section>
  );
};
